import React from 'react'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import {
  DAY_TYPE_BORDER_COLORS,
  DAY_TYPE_COLORS,
  DAY_TYPE_STRIPE_COLORS,
} from '@/modules/drivers/tabs/registry/schedule/constants/DayColors'

const BORDER_RADIUS = 6

interface CellItemProps {
  cell: any
  dayTypeName?: string
  width: number
  work: boolean
  index: number
  itemsCount: number
  isFirstRow?: boolean
  isLastRow?: boolean
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
  isSelected?: boolean
  isFirstSelected?: boolean
  isLastSelected?: boolean
  isRowSelected?: boolean
  isFirstRowSelected?: boolean
  isLastRowSelected?: boolean
  isSummaryHighlighted?: boolean
  isModified?: boolean
  isColumnHovered?: boolean
  isRowHovered?: boolean
  onClick?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseEnter?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}

const CellItem = React.memo<CellItemProps>(
  ({
    cell,
    dayTypeName,
    width,
    work,
    index,
    itemsCount,
    isFirstRow = false,
    isLastRow = false,
    isFirstInGroup = false,
    isLastInGroup = false,
    isSelected = false,
    isFirstSelected = false,
    isLastSelected = false,
    isRowSelected = false,
    isFirstRowSelected = false,
    isLastRowSelected = false,
    isSummaryHighlighted = false,
    isModified = false,
    isColumnHovered = false,
    isRowHovered = false,
    onClick,
    onMouseDown,
    onMouseEnter,
    onContextMenu,
  }) => {
    const isSummaryColumn = index >= itemsCount - 2
    const bgColor = isSummaryColumn
      ? 'var(--ts-summary-bg)'
      : (DAY_TYPE_COLORS[cell?.dayType] ?? '#E7ECF0')
    const borderColor = isSummaryColumn
      ? 'var(--et-color-base-300)'
      : (DAY_TYPE_BORDER_COLORS[cell?.dayType] ?? '#D9D9D9')
    const stripeColor = DAY_TYPE_STRIPE_COLORS[cell?.dayType] ?? '#D9D9D9'

    const sel = isSelected && !isSummaryColumn
    const rowSel = isRowSelected && !isSummaryColumn
    const summarySel = isSummaryColumn && isSummaryHighlighted
    const selColor = '#2C57FF'

    const modified = isModified
    const toolTip = isSummaryColumn
      ? ''
      : [dayTypeName, ...String(cell?.toolTip ?? '').split(/\r?\n/)]
          .filter(Boolean)
          .join('; ')

    return (
      <div
        style={{
          minWidth: isSummaryColumn ? width + 24 : width,
          flex: 1,
          height: '100%',
          background: summarySel ? 'var(--ts-summary-bg-selected)' : bgColor,
          borderTopColor: 'transparent',
          borderBottomColor: summarySel
            ? 'transparent'
            : sel
              ? isLastRow
                ? selColor
                : 'transparent'
              : borderColor,
          borderLeftColor: summarySel
            ? 'transparent'
            : sel
              ? isFirstSelected
                ? selColor
                : 'transparent'
              : borderColor,
          borderRightColor: summarySel
            ? 'transparent'
            : sel
              ? isLastSelected
                ? selColor
                : 'transparent'
              : borderColor,
          borderTopLeftRadius: sel
            ? 0
            : !isSummaryColumn && isFirstInGroup
              ? BORDER_RADIUS
              : 0,
          borderTopRightRadius: sel
            ? 0
            : !isSummaryColumn && isLastInGroup
              ? BORDER_RADIUS
              : 0,
          borderBottomLeftRadius:
            sel && isLastRow && isFirstSelected ? BORDER_RADIUS : 0,
          borderBottomRightRadius:
            sel && isLastRow && isLastSelected ? BORDER_RADIUS : 0,
          borderTopWidth: isFirstRow ? 0 : 1,
          borderBottomWidth: 1,
          boxShadow: modified ? 'inset 0 0 0 2px #2448BB' : undefined,
        }}
        className={`tw-relative tw-grid tw-cursor-pointer tw-select-none tw-place-items-center tw-border-[1px] tw-border-solid tw-text-[10px] ${isSummaryColumn ? 'tw-text-content-base' : 'tw-text-[#1F2937]'}`}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
      >
        {(summarySel ||
          ((isSelected || isRowSelected) && !isSummaryColumn)) && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[#0034FF]/20" />
        )}
        {!isSelected &&
          !isRowSelected &&
          !summarySel &&
          (isColumnHovered || isRowHovered) &&
          !isSummaryColumn && (
            <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[#0034FF]/10" />
          )}
        {isFirstRow && !isSummaryColumn && (
          <div
            className="tw-absolute tw-bottom-0 tw-h-[3px] tw-w-full"
            style={{ background: stripeColor }}
          />
        )}
        {toolTip ? (
          // WPF: status dnia w dymku; typ dnia z wykrzyknikiem ("P!"), gdy dzień
          // nie jest dniem bez ostrzeżeń.
          <Tooltip
            text={toolTip}
            portal
            asChild
            maxWidth="320px"
          >
            {cell.hasWarning ? (
              <p className="tw-font-semibold tw-text-red-600">{cell.value}!</p>
            ) : (
              <p className={`${work ? 'tw-font-medium' : 'tw-font-normal'}`}>
                {cell.value}
              </p>
            )}
          </Tooltip>
        ) : (
          <p className={`${work ? 'tw-font-medium' : 'tw-font-normal'}`}>
            {cell?.value}
          </p>
        )}
      </div>
    )
  },
)

CellItem.displayName = 'CellItem'

export default CellItem
